/* eslint max-classes-per-file: ["error", 2] */

'use strict';

define('vb/extensions/dynamic/private/types/blobMetadataProviderDescriptor',[
  'knockout',
  'ojs/ojcontext',
  'vb/extensions/dynamic/private/types/abstractMetadataProviderDescriptor',
  'vb/extensions/dynamic/private/types/dynamicLayoutMetadataProviderDescriptor',
  'vb/extensions/dynamic/private/types/heterogeneousMetadataProviderDescriptor',
  'vb/extensions/dynamic/private/helpers/layoutDeserializer',
], (
  ko,
  ojContext,
  AbstractMetadataProviderDescriptor,
  DynamicLayoutMetadataProviderDescriptor,
  HeterogeneousMetadataProviderDescriptor,
  LayoutDeserializer) => {
  /** @typedef {import('vb/extensions/dynamic/private/helpers/layoutSerializer').default} LayoutSerializer */

  /**
   * A metadata provider descriptor for blob metadata.
   * @extends AbstractMetadataProviderDescriptor
   */
  class BlobMetadataProviderDescriptor extends AbstractMetadataProviderDescriptor {
    /**
     * Creates a factory bridge for the metadata provider.
     *
     * @returns {Object} The factory bridge created by the delegate metadata provider descriptor.
     */
    createFactoryBridge() {
      return this.delegate.createFactoryBridge();
    }

    /**
     * Creates options for the metadata provider.
     *
     * @returns {Object} The options created by the delegate metadata provider descriptor.
     */
    createOptions() {
      return this.delegate.createOptions(this.options);
    }

    /**
     * This method is called during the activation phase of the descriptor. During this phase,
     * it is safe to access values containing expressions to other variables.
     *
     * @returns {Promise}
     */
    activate() {
      return Promise.resolve().then(() => {
        const value = this.getValue();

        if (!value.blob) {
          // create a computed observable to wait for the blob to become available
          this.activationComputed = ko.computed(() => {
            const v = this.getValue();
            if (!this.provider && v.blob && v.hash !== undefined) {
              // prevent any further update
              this.activationComputed.dispose();
              delete this.activationComputed;

              // Need to add a busyState for webdriver tests to pass because the metadata
              // provider is created asynchronously.
              const busyContext = ojContext.getPageContext().getBusyContext();
              const busyStateResolver = busyContext.addBusyState({
                description: 'Activating BlobMetadataProviderDescriptor',
              });

              // call activate again to create the metadata provider
              this.activate().finally(busyStateResolver);
            }
          });
          return undefined;
        }

        this.deserializer = new LayoutDeserializer(value.blob, value.hash);
        return this.deserializer.deserialize().then(() => {
          this.options = this.deserializer.getOptions();

          this.delegate = this.options.discriminator ? new HeterogeneousMetadataProviderDescriptor()
            : new DynamicLayoutMetadataProviderDescriptor();

          return super.activate();
        });
      });
    }

    /**
     * Overridden to not return an instance of LayoutSerializer for the BlobMetadataProviderDescriptor.
     *
     * @param {Object} options
     * @returns {LayoutSerializer|null}
     */
    // eslint-disable-next-line class-methods-use-this, no-unused-vars
    createSerializer(options) {
      return null;
    }

    /**
     * Creates a deserializer instance for the blob metadata.
     *
     * @returns {LayoutDeserializer} The deserializer instance used for deserializing the blob metadata.
     */
    createDeserializer() {
      return this.deserializer;
    }

    /**
     * Overrides base implementation to return the type definition for the BlobMetadataProviderDescriptor.
     *
     * @see AbstractMetadataProviderDescriptor#getTypeDefinition
     * @returns {{type: {blob: 'any', hash: 'number', objectContext: 'any', externalValue: 'any', initialValue: 'any'}}}
     *          An object containing the type definition, with properties describing the expected structure of the
     *          metadata.
     */
    // eslint-disable-next-line class-methods-use-this
    getTypeDefinition() {
      return {
        type: {
          blob: 'any',
          hash: 'number',
          objectContext: 'any',
          externalValue: 'any',
          initialValue: 'any',
        },
      };
    }
  }

  return BlobMetadataProviderDescriptor;
});

